import { useState, type ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../../components/ui/Avatar';
import { Icon } from '../../components/ui/Icon';
import { formatCount } from '../../utils/format';
import './post-card.css';

/** Label/value fact shown under the post, e.g. a report's reason. */
export interface PostCardFact {
  label: string;
  value: ReactNode;
}

export interface PostCardEngagement {
  likes: number;
  comments: number;
  shares: number;
}

interface PostCardProps {
  authorName: string;
  authorAvatar?: string | null;
  /** Second line under the name: timestamp, group, weight — already formatted. */
  meta?: readonly (string | null | undefined)[];
  /** Status pills; rendered top-right of the header. */
  badges?: ReactNode;
  title?: string;
  text?: string;
  images?: readonly string[];
  videoUrl?: string | null;
  engagement?: PostCardEngagement;
  /**
   * Context an operator needs that the member never sees (report reason,
   * reviewer notes). `factsTone` tints the block so a report reads as a warning
   * rather than as part of the post.
   */
  facts?: readonly PostCardFact[];
  factsTone?: 'neutral' | 'danger';
  selection?: {
    selected: boolean;
    onToggle: () => void;
  };
  /** Moderation controls for this post. */
  actions?: ReactNode;
}

/** Beyond this the caption is collapsed behind a show-more toggle. */
const CLAMP_THRESHOLD = 420;

/**
 * A post as the member sees it, at admin scale.
 *
 * The anatomy is the member feed's — avatar, author, caption, media, engagement
 * — because a moderator has to judge the post the way it was published, not as
 * a table row. Only the media cap is smaller, and the two blocks the member has
 * no equivalent for (operator facts and moderation actions) are appended
 * *after* the post so they can never be mistaken for its content.
 */
export function PostCard({
  authorName,
  authorAvatar,
  meta,
  badges,
  title,
  text,
  images = [],
  videoUrl,
  engagement,
  facts,
  factsTone = 'neutral',
  selection,
  actions,
}: PostCardProps) {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  const body = text?.trim() ?? '';
  const clampable = body.length > CLAMP_THRESHOLD;
  const metaLine = (meta ?? []).filter(Boolean).join(' · ');
  const gallery = images.slice(0, 6);
  const overflow = images.length - gallery.length;

  return (
    <article
      className="admin-post"
      data-selected={selection?.selected ? 'true' : undefined}>
      <header className="admin-post__header">
        {selection ? (
          <input
            type="checkbox"
            className="ui-table__checkbox"
            checked={selection.selected}
            onChange={selection.onToggle}
            aria-label={t('common.selectRowNamed', { name: authorName })}
          />
        ) : null}
        <Avatar src={authorAvatar ?? null} name={authorName} size="md" />
        <div className="admin-post__author">
          <span className="admin-post__name">{authorName}</span>
          {metaLine ? <span className="admin-post__meta">{metaLine}</span> : null}
        </div>
        {badges ? <div className="admin-post__badges">{badges}</div> : null}
      </header>

      {title ? <h3 className="admin-post__title">{title}</h3> : null}

      {body ? (
        <div className="admin-post__body">
          <p
            className={`admin-post__text${
              clampable && !expanded ? ' admin-post__text--clamped' : ''
            }`}>
            {body}
          </p>
          {clampable ? (
            <button
              type="button"
              className="admin-post__more"
              aria-expanded={expanded}
              onClick={() => setExpanded(open => !open)}>
              {t(expanded ? 'posts.card.showLess' : 'posts.card.showMore')}
            </button>
          ) : null}
        </div>
      ) : null}

      {videoUrl ? (
        <div className="admin-post__media">
          {/* Metadata only: a moderation queue must not start streaming a dozen
              videos, and the operator presses play on the one they are judging. */}
          <video className="admin-post__video" src={videoUrl} controls preload="metadata" />
        </div>
      ) : gallery.length > 1 ? (
        <div className={`admin-post__gallery admin-post__gallery--${gallery.length}`}>
          {gallery.map((src, index) => (
            <a
              key={src}
              className="admin-post__gallery-item"
              href={src}
              target="_blank"
              rel="noreferrer"
              /* The image is decorative (no alt text exists for it), so the
                 link needs its own name. */
              aria-label={t('posts.card.openMedia')}
              title={t('posts.card.openMedia')}>
              <img src={src} alt="" aria-hidden="true" loading="lazy" decoding="async" />
              {index === gallery.length - 1 && overflow > 0 ? (
                <span className="admin-post__gallery-overlay" aria-hidden="true">
                  {t('posts.card.moreImages', { count: overflow })}
                </span>
              ) : null}
            </a>
          ))}
        </div>
      ) : gallery.length === 1 ? (
        <a
          className="admin-post__media"
          href={gallery[0]}
          target="_blank"
          rel="noreferrer"
          aria-label={t('posts.card.openMedia')}
          title={t('posts.card.openMedia')}>
          <img
            className="admin-post__image"
            src={gallery[0]}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
        </a>
      ) : null}

      {engagement ? (
        <div className="admin-post__engagement">
          <span className="admin-post__stat">
            <Icon name="heart" size="sm" tone="rose" />
            {formatCount(engagement.likes)}
            <span className="sr-only"> {t('posts.card.likes')}</span>
          </span>
          <span className="admin-post__stat">
            <Icon name="comment" size="sm" tone="blue" />
            {formatCount(engagement.comments)}
            <span className="sr-only"> {t('posts.card.comments')}</span>
          </span>
          <span className="admin-post__stat">
            <Icon name="share" size="sm" tone="violet" />
            {formatCount(engagement.shares)}
            <span className="sr-only"> {t('posts.card.shares')}</span>
          </span>
        </div>
      ) : null}

      {facts && facts.length > 0 ? (
        <dl className={`admin-post__facts admin-post__facts--${factsTone}`}>
          {facts.map(fact => (
            <div className="admin-post__fact" key={fact.label}>
              <dt className="admin-post__fact-label">{fact.label}</dt>
              <dd className="admin-post__fact-value">
                {fact.value === '' || fact.value == null ? t('common.none') : fact.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {actions ? <footer className="admin-post__actions">{actions}</footer> : null}
    </article>
  );
}
